import { useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import { useAppSelector } from "redux/hooks";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import ToolTips from "utils/toolTips";
import CustomHref from "components/core/blocks/CustomHref";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "utils/api";
import { DSP_PROFILE_ONBOARDING_STATUS_FILTER } from "views/advertising/orgLifecycleFilters";
import { successToast, errorToast } from "../../../utils/toasts";
import { TextField } from "components/fields/base/TextField";
import { Flex } from "../../../../styled-system/jsx/flex.mjs";
import { Button } from "components/core/basic/Button";
import { Modal } from "components/core/basic/RACModal";
import { css } from "../../../../styled-system/css";

const DSPProfilesTable = ({
  tableData,
  columnsToRender,
  isLoading,
  additionalColumns = [],
  bulkActionOptions = [],
  selectedProfiles = [],
  setSelectedProfiles = () => {},
  isSelectable = false,
  defaultSorted,
  additionalFilters,
}) => {
  const [selectedRowForOnboarding, setSelectedRowForOnboarding] = useState(null);
  const [dspProfileNameConfirmation, setDspProfileNameConfirmation] = useState("");
  const [trellisOrgNameConfirmation, setTrellisOrgNameConfirmation] = useState("");

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const tableRef = useRef();
  const queryClient = useQueryClient();

  const TABLE_ID = "trellisAmazonDSPPRofilesTable";

  const onboardDSPProfile = useMutation({
    mutationFn: async ({ advertiserId, clientId, marketPlace }) => {
      const onboardRequestData = {
        dsp_advertiser_id: advertiserId,
        destination_org: parseInt(user.organization_id),
        dsp_client_id: clientId,
        marketplace: marketPlace,
      };

      return await api.post("/api/dsp/profiles/", onboardRequestData);
    },

    onSuccess: (data) => {
      setSelectedRowForOnboarding(null);

      successToast({
        message: "DSP profile successfully onboarded",
      });

      queryClient.invalidateQueries({
        queryKey: ["dspProfiles"],
      });
      return data;
    },

    onError: (error) => {
      const errorMessage = error?.response?.data ?? "Failed to onboard DSP profile";
      errorToast({ message: errorMessage, error });
    },
  });

  const columnOptions = [
    buildTextColumn("dsp_client_name", "DSP Client Name", { width: 200 }),
    buildTextColumn("dsp_advertiser_id", "DSP Advertiser ID", { width: 200, checked: false }),
    buildTextColumn("dsp_client_account_id", "DSP Client ID", { width: 200, checked: false }),
    {
      key: "url",
      compareKey: null,
      options: {
        metric: {
          id: "url",
          format: "text",
          accessor: (d) => d,
          name: "Store Page",
        },
        formatter: (row) => (
          <>
            <a href={row.url} target="_blank" rel="noreferrer">
              Visit Store
            </a>
            <ToolTips id={row.store_page_id} toolTip={`Store Page ID: ${row.store_page_id}`} />
          </>
        ),
        hideFilter: true,
      },
    },
    buildTextColumn("country", "Country", { formatter: (v) => (v === "US" ? "USA" : v), checked: false }),
    buildTextColumn("onboarded", "Onboarded", { formatter: (v) => (v ? "Yes" : "No") }),
    {
      key: "onboarded_org_id",
      compareKey: null,
      options: {
        metric: {
          id: "onboarded_org_id",
          format: "text",
          accessor: (d) => d,
          name: "Active Org ID",
        },
        formatter: (row) =>
          row.onboarded && row.onboarded_org_id ?
            <span>
              {row.onboarded_org_id}{" "}
              <ToolTips id={`${row.onboarded_org_id}`} toolTip={`${row.onboarded_org_name ?? ""}`} />
            </span>
          : null,
        hideFilter: true,
      },
    },
    {
      key: "suggested_org_id",
      compareKey: null,
      options: {
        metric: {
          id: "suggested_org_id",
          format: "text",
          accessor: (d) => d,
          name: "Suggested Org ID",
        },
        formatter: (row) =>
          !row.onboarded && row.suggested_org_id ?
            <span>
              {row.suggested_org_id}{" "}
              <ToolTips id={`${row.suggested_org_id}`} toolTip={`${row.suggested_org_name ?? ""}`} />
            </span>
          : null,
        hideFilter: true,
      },
    },
    {
      key: "actions",
      compareKey: null,
      options: {
        metric: {
          id: "actions",
          format: "text",
          accessor: (d) => d,
          name: "Actions",
          options: { width: 230 },
        },
        formatter: (row) =>
          !row.onboarded ?
            <Button
              variant="primary"
              onClick={() => {
                setSelectedRowForOnboarding(row);
                setDspProfileNameConfirmation("");
                setTrellisOrgNameConfirmation("");
              }}
            >
              Onboard Current Org
            </Button>
          : null,
        hideFilter: true,
      },
    },

    ...additionalColumns,
  ];

  const columnSpecs = columnsToRender.map((renderColumnKey) =>
    columnOptions.find((option) => option.key === renderColumnKey),
  );

  const ADDITIONAL_FILTERS = [DSP_PROFILE_ONBOARDING_STATUS_FILTER];
  const filterProps = useCustomTableFilters(TABLE_ID);

  return (
    <Row className="mx-0 w-100">
      <Col xs={12} className="mx-auto px-0">
        {selectedRowForOnboarding && (
          <Modal
            isOpen={!!selectedRowForOnboarding}
            onOpenChange={(isOpen) => {
              if (!isOpen) setSelectedRowForOnboarding(null);
            }}
            title={"Confirm DSP Profile Onboarding"}
            className={css({ maxWidth: "1000px" })}
          >
            <Flex direction="column" gap={"medium"} className={css({ padding: "xlarge" })}>
              <p className="fs-4 mx-auto">
                Are you sure you want to onboard the DSP profile for <strong>{selectedRowForOnboarding.name}</strong> to{" "}
                <strong>{user.organization_name}</strong>?
              </p>
              <p className="fs-5 text-start">
                <strong>Recommended: </strong>To ensure the DSP profile lines up with this account, please visit the
                brand's <CustomHref link={selectedRowForOnboarding.url} text="store home page" type="target" /> and
                verify that at least one ASIN from the store matches a product on Trellis through the app searchbar.
              </p>
              <Flex gap={"large"}>
                <TextField
                  value={dspProfileNameConfirmation}
                  onChange={(value) => setDspProfileNameConfirmation(value)}
                  label={"Please enter the DSP profile name to confirm onboarding"}
                  placeholder={selectedRowForOnboarding.dsp_profile_name}
                />
                <TextField
                  value={trellisOrgNameConfirmation}
                  onChange={(value) => setTrellisOrgNameConfirmation(value)}
                  label={"Please enter the Trellis org name to confirm onboarding"}
                  placeholder={user.organization_name}
                />
              </Flex>
              <Flex justifyContent={"center"} gap={"large"}>
                <Button
                  variant={"secondary"}
                  onPress={() => {
                    setSelectedRowForOnboarding(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onPress={() => {
                    onboardDSPProfile.mutate({
                      advertiserId: selectedRowForOnboarding.dsp_advertiser_id,
                      clientId: selectedRowForOnboarding.client_id,
                      marketPlace: selectedRowForOnboarding.country,
                    });
                  }}
                  isDisabled={
                    dspProfileNameConfirmation !== selectedRowForOnboarding.dsp_profile_name ||
                    trellisOrgNameConfirmation !== user.organization_name
                  }
                >
                  Confirm
                </Button>
              </Flex>
            </Flex>
          </Modal>
        )}

        <BulkActionsTable
          marketPlace={marketPlace}
          tableRef={tableRef}
          tableId={TABLE_ID}
          titleCol={{
            id: "name",
            isStatic: true,
            Header: "Name",
            accessor: "name",
            style: { width: "280px" },
            width: 280,
            Cell: ({ row }) => {
              const { name } = row._original;

              return <div className="d-flex align-items-center overflow-ellipsis">{name}</div>;
            },
            filterMethod: () => {
              return;
            },
            Filter: () => null,
          }}
          columnSpecs={columnSpecs}
          data={tableData}
          bulkActionOptions={bulkActionOptions}
          filterWidget={true}
          hideFilterToggleIcon={true}
          isSelectable={isSelectable}
          isExpandable={false}
          selected={selectedProfiles}
          setSelected={setSelectedProfiles}
          selectionKey={"id"}
          defaultSorted={defaultSorted || [{ id: "id", desc: false }]}
          {...filterProps}
          additionalFilterOptions={ADDITIONAL_FILTERS}
          excludedFilterColumns={["onboard"]}
          getSearchCriteria={(row) => {
            const { advertiserId, country, currency, dsp_profile_name, store_page_id, url } = row;
            return `${advertiserId} ${country} ${currency} ${dsp_profile_name} ${store_page_id} ${url}`.toLowerCase();
          }}
          tableRowDescription={"User"}
          minRows={0}
          isLoading={isLoading}
          defaultPageSize={5}
          additionalFilters={additionalFilters}
        />
      </Col>
    </Row>
  );
};

export default DSPProfilesTable;
